'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import type { SanityMedia } from '../../../../types';
import { resolveMedia } from '../../../MediaAsset';

const FALLBACK_VIDEOS = [
  'https://image01.cf.vidu.studio/vidu/media-asset/vidu-video-3221989128810847-9c46f530.mp4',
  'https://image01.cf.vidu.studio/vidu/media-asset/vidu-video-3221989396917088-071eced2.mp4',
  'https://image01.cf.vidu.studio/vidu/media-asset/vidu-video-3221989184124284-2d53c41e.mp4',
  'https://image01.cf.vidu.studio/vidu/media-asset/vidu-video-3221989577942753-b05af642.mp4',
];

const CROSSFADE_DURATION = 1.2;

interface HeroBackgroundProps {
  media?: SanityMedia | null;
}

export function HeroBackground({ media }: HeroBackgroundProps) {
  const resolved = resolveMedia(media);
  const videoUrl = resolved?.kind === 'video' ? resolved.url : null;
  const imageUrl = resolved?.kind === 'image' ? resolved.url : null;
  const coverUrl = resolved?.coverUrl;

  return (
    <>
      {/* Background media with fade-in on mount */}
      <motion.div
        animate={{ opacity: 1 }}
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
      >
        {videoUrl ? (
          <VideoLazyLoad
            className="h-full w-full object-cover"
            poster={coverUrl ?? ''}
            videoClassName="h-full w-full object-cover"
            videoSrc={videoUrl}
            alt="AI video generation showcase"
          />
        ) : imageUrl ? (
          <img alt="AI video generation background" className="h-full w-full object-cover" src={imageUrl} />
        ) : (
          <FallbackVideoLoop />
        )}
      </motion.div>

      {/* Left gradient overlay */}
      <motion.div
        animate={{ opacity: 1 }}
        className="pointer-events-none absolute top-0 left-0 h-full w-[54%] bg-gradient-to-r from-black to-transparent sm:w-full"
        initial={{ opacity: 0 }}
        transition={{ duration: 2, ease: 'easeOut', delay: 0.3 }}
      />

      {/* Bottom gradient overlay */}
      <motion.div
        animate={{ opacity: 1 }}
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-t from-black to-transparent"
        initial={{ opacity: 0 }}
        transition={{ duration: 2, ease: 'easeOut', delay: 0.3 }}
      />
    </>
  );
}

function FallbackVideoLoop() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);

  const handleEnded = useCallback(() => {
    setNextIndex((currentIndex + 1) % FALLBACK_VIDEOS.length);
  }, [currentIndex]);

  useEffect(() => {
    if (nextIndex === null) return;
    const timer = setTimeout(() => {
      setCurrentIndex(nextIndex);
      setNextIndex(null);
    }, CROSSFADE_DURATION * 1000);
    return () => clearTimeout(timer);
  }, [nextIndex]);

  return (
    <div className="relative h-full w-full">
      <AnimatePresence>
        <motion.video
          key={currentIndex}
          animate={{ opacity: 1 }}
          autoPlay
          className="absolute inset-0 h-full w-full object-cover"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          muted
          onEnded={handleEnded}
          playsInline
          src={FALLBACK_VIDEOS[currentIndex]}
          transition={{ duration: CROSSFADE_DURATION, ease: 'easeInOut' }}
        />
        {nextIndex !== null && (
          <motion.video
            key={nextIndex}
            animate={{ opacity: 1 }}
            autoPlay
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0 }}
            muted
            playsInline
            src={FALLBACK_VIDEOS[nextIndex]}
            transition={{ duration: CROSSFADE_DURATION, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
